const CLASS_ACTIVE = 'is-active';

export default class GalleryModal {
    current = 0;

    static instance(){
        if(this._instance) return this._instance;
        return this._instance = new this();
    }

    constructor() {
        this.el = document.createElement('div');
        this.content = document.createElement('div');
        this.cross = document.createElement('div');
        this.left = document.createElement('div');
        this.right = document.createElement('div');
        this.el.className = 'gallery-modal';
        this.content.className = 'gallery-modal__content';
        this.cross.className = 'gallery-modal__cross';
        this.left.className = 'gallery-modal__left';
        this.right.className = 'gallery-modal__right';
        
        // Add accessibility attributes
        this.el.setAttribute('role', 'dialog');
        this.el.setAttribute('aria-modal', 'true');
        this.el.setAttribute('aria-label', 'Gallery Image Viewer');
        this.el.setAttribute('tabindex', '-1');
        
        // Make navigation buttons focusable
        this.cross.setAttribute('role', 'button');
        this.cross.setAttribute('aria-label', 'Close gallery');
        this.cross.setAttribute('tabindex', '0');
        
        this.left.setAttribute('role', 'button');
        this.left.setAttribute('aria-label', 'Previous image');
        this.left.setAttribute('tabindex', '0');
        
        this.right.setAttribute('role', 'button');
        this.right.setAttribute('aria-label', 'Next image');
        this.right.setAttribute('tabindex', '0');
        
        this.items = [];
        this.previouslyFocused = null;
        this.focusableElements = [];
        
        this.el.appendChild(this.content);
        this.el.appendChild(this.cross);
        this.el.appendChild(this.left);
        this.el.appendChild(this.right);
        document.body.appendChild(this.el);

        this.close = this.close.bind(this);
        this.goLeft = this.goLeft.bind(this);
        this.goRight = this.goRight.bind(this);
        this.keyDown = this.keyDown.bind(this);
        this.handleKeyboardNavigation = this.handleKeyboardNavigation.bind(this);
        this.init();
    }

    init() {
        this.cross.addEventListener('click', this.close);
        this.left.addEventListener('click', this.goLeft);
        this.right.addEventListener('click', this.goRight);
        
        // Add keyboard event handlers for buttons
        this.cross.addEventListener('keydown', this.handleKeyboardNavigation);
        this.left.addEventListener('keydown', this.handleKeyboardNavigation);
        this.right.addEventListener('keydown', this.handleKeyboardNavigation);
        this.el.addEventListener('keydown', this.handleKeyboardNavigation);
        
        document.addEventListener("keydown", this.keyDown);
    }

    createItem(index, imgAttr){
        const node = document.createElement('div');
        const img = document.createElement('img');
        node.className = 'gallery-modal__item';
        img.src = imgAttr;
        node.appendChild(img);
        this.content.appendChild(node);
        this.items[index] = node;
    }

    clean() {
        this.content.innerHTML = '';
        this.items = [];
    }

    open(index) {
        this.current = index;
        
        // Store currently focused element
        this.previouslyFocused = document.activeElement;
        
        // Hide other content from screen readers
        this.hideOtherContent();
        
        this.el.classList.add(CLASS_ACTIVE);
        this.items[this.current].classList.add(CLASS_ACTIVE);
        
        // Set up focusable elements
        this.updateFocusableElements();
        
        // Focus the close button initially
        setTimeout(() => {
            this.cross.focus();
        }, 100);
    }

    close() {
        this.el.classList.remove(CLASS_ACTIVE);
        this.items[this.current].classList.remove(CLASS_ACTIVE);
        
        // Show other content to screen readers
        this.showOtherContent();
        
        // Return focus to previously focused element
        if (this.previouslyFocused) {
            this.previouslyFocused.focus();
        }
    }

    goLeft() {
        let newIndex = this.current - 1;
        newIndex = newIndex < 0 ? this.items.length - 1 : newIndex;
        this.items[this.current].classList.remove(CLASS_ACTIVE);
        this.current = newIndex;
        this.items[this.current].classList.add(CLASS_ACTIVE);
    }

    goRight() {
        let newIndex = this.current + 1;
        newIndex = newIndex > this.items.length - 1 ? 0 : newIndex;
        this.items[this.current].classList.remove(CLASS_ACTIVE);
        this.current = newIndex;
        this.items[this.current].classList.add(CLASS_ACTIVE);
    }

    keyDown(e) {
        // Only handle when modal is active
        if (!this.el.classList.contains(CLASS_ACTIVE)) return;
        
        // Esc
        if (e.which === 27) {
            e.preventDefault();
            this.close();
        }

        // Left arrow
        if (e.which === 37) {
            e.preventDefault();
            this.goLeft();
        }

        // Right arrow
        if (e.which === 39) {
            e.preventDefault();
            this.goRight();
        }
    }

    handleKeyboardNavigation(e) {
        // Only handle when modal is active
        if (!this.el.classList.contains(CLASS_ACTIVE)) return;

        // Handle Enter and Space for button activation
        if (e.key === 'Enter' || e.key === ' ') {
            if (e.target === this.cross) {
                e.preventDefault();
                this.close();
            } else if (e.target === this.left) {
                e.preventDefault();
                this.goLeft();
            } else if (e.target === this.right) {
                e.preventDefault();
                this.goRight();
            }
            return;
        }

        // Handle Tab for focus trapping
        if (e.key === 'Tab') {
            this.trapFocus(e);
        }
    }

    updateFocusableElements() {
        this.focusableElements = [this.cross, this.left, this.right].filter(el => el);
    }

    trapFocus(e) {
        if (this.focusableElements.length === 0) {
            e.preventDefault();
            return;
        }

        const firstElement = this.focusableElements[0];
        const lastElement = this.focusableElements[this.focusableElements.length - 1];

        if (e.shiftKey) {
            // Shift + Tab (backward)
            if (document.activeElement === firstElement) {
                e.preventDefault();
                lastElement.focus();
            }
        } else {
            // Tab (forward)
            if (document.activeElement === lastElement) {
                e.preventDefault();
                firstElement.focus();
            }
        }
    }

    hideOtherContent() {
        const allElements = document.body.children;
        for (let element of allElements) {
            if (element !== this.el && !this.el.contains(element)) {
                if (!element.hasAttribute('aria-hidden')) {
                    element.setAttribute('aria-hidden', 'true');
                    element.setAttribute('data-gallery-hidden', 'true');
                }
            }
        }
    }

    showOtherContent() {
        const hiddenElements = document.querySelectorAll('[data-gallery-hidden]');
        for (let element of hiddenElements) {
            element.removeAttribute('aria-hidden');
            element.removeAttribute('data-gallery-hidden');
        }
    }
}